import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import {
  MessageCircle, Settings, Plus, Trash2, Play, Pause, Loader2, X, Save, RefreshCw,
  Send, Clock, Upload, Users, ChevronRight, Edit2, CheckCircle2, XCircle, AlertCircle,
} from 'lucide-react';
import { api } from '../lib/api';
import { authService } from '../lib/auth';

const emptyCampaign = () => ({
  name: '',
  template_name: '',
  template_language: 'hi',
  body_params: [],
  header_image: '',
  button_url: '',
  recipient_filter: {
    sources: ['leads'],
    lead_status: '',
    lead_course: '',
    lead_courses: [],
    lead_from_date: '',
    lead_to_date: '',
    lead_assigned_to: '',
    group_ids: [],
    extra_phones: [],
    dedupe: true,
  },
  schedule_mode: 'now',
  scheduled_at: '',
  recurrence: { frequency: 'daily', time: '09:00', days_of_week: [], day_of_month: 1 },
});

export default function WhatsAppMarketing() {
  const user = authService.getCurrentUser();
  const isPrivileged = ['admin', 'manager'].includes(user?.role);
  const [subtab, setSubtab] = useState('campaigns'); // 'campaigns' | 'templates' | 'settings'

  return (
    <div className="space-y-4" data-testid="whatsapp-marketing">
      <div className="flex items-center gap-2 border-b border-slate-200 pb-2">
        {[
          { id: 'campaigns', label: 'Campaigns', icon: Send },
          { id: 'templates', label: 'Templates', icon: MessageCircle },
          ...(isPrivileged ? [{ id: 'settings', label: 'Settings', icon: Settings }] : []),
        ].map((t) => (
          <button
            key={t.id}
            data-testid={`wa-tab-${t.id}`}
            onClick={() => setSubtab(t.id)}
            className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md ${
              subtab === t.id ? 'bg-emerald-50 text-emerald-700' : 'text-slate-600 hover:bg-slate-50'
            }`}
          >
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </div>
      {subtab === 'campaigns' && <CampaignsTab isPrivileged={isPrivileged} />}
      {subtab === 'templates' && <TemplatesTab isPrivileged={isPrivileged} />}
      {subtab === 'settings' && isPrivileged && <SettingsTab />}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SETTINGS TAB
// ─────────────────────────────────────────────────────────────────────────────
function SettingsTab() {
  const [form, setForm] = useState({ api_key: '', sender: '', base_url: '', default_language: 'hi' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [masked, setMasked] = useState('');
  const [configured, setConfigured] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.getWaSettings();
      setForm((f) => ({ ...f, sender: data.sender || '', base_url: data.base_url || '', default_language: data.default_language || 'hi', api_key: '' }));
      setMasked(data.api_key_masked || '');
      setConfigured(!!data.configured);
    } catch (e) { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await api.updateWaSettings(form);
      toast.success('WhatsApp settings saved');
      await load();
      setForm((f) => ({ ...f, api_key: '' }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  if (loading) return <div className="py-8 text-center text-slate-500"><Loader2 className="mx-auto animate-spin" /></div>;

  return (
    <div className="max-w-2xl bg-white rounded-xl border border-slate-200 p-5 space-y-4" data-testid="wa-settings">
      <h3 className="text-base font-semibold text-slate-800 flex items-center gap-2">
        <Settings size={16} /> Provider Settings (Onexaura)
      </h3>
      <div className={`text-xs rounded-md px-3 py-2 ${configured ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
        {configured ? 'Configured & ready to send.' : 'Not configured yet — enter API key and sender number below to enable sending.'}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="text-sm">
          <span className="block text-slate-600 mb-1">API Key</span>
          <input type="password" value={form.api_key} placeholder={masked ? `Current: ${masked} — leave blank to keep` : 'Paste API key'}
                 onChange={(e) => setForm({ ...form, api_key: e.target.value })}
                 data-testid="wa-settings-api-key"
                 className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm focus:ring-2 focus:ring-emerald-500" />
        </label>
        <label className="text-sm">
          <span className="block text-slate-600 mb-1">Sender number (with country code, no +)</span>
          <input type="text" value={form.sender} placeholder="e.g. 917065060600"
                 onChange={(e) => setForm({ ...form, sender: e.target.value })}
                 data-testid="wa-settings-sender"
                 className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm focus:ring-2 focus:ring-emerald-500" />
        </label>
        <label className="text-sm">
          <span className="block text-slate-600 mb-1">Base URL</span>
          <input type="text" value={form.base_url} placeholder="https://api.onexaura.com"
                 onChange={(e) => setForm({ ...form, base_url: e.target.value })}
                 className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm focus:ring-2 focus:ring-emerald-500" />
        </label>
        <label className="text-sm">
          <span className="block text-slate-600 mb-1">Default template language</span>
          <select value={form.default_language} onChange={(e) => setForm({ ...form, default_language: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm bg-white">
            <option value="hi">Hindi (hi)</option>
            <option value="en">English (en)</option>
            <option value="en_US">English US (en_US)</option>
          </select>
        </label>
      </div>
      <button onClick={save} disabled={saving} data-testid="wa-settings-save"
              className="px-4 py-2 bg-emerald-600 text-white rounded-md text-sm font-medium hover:bg-emerald-700 disabled:opacity-60 inline-flex items-center gap-2">
        {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
        Save Settings
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TEMPLATES TAB
// ─────────────────────────────────────────────────────────────────────────────
function TemplatesTab({ isPrivileged }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.listWaTemplates();
      setList(data || []);
    } catch (e) { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setShowModal(true); };
  const openEdit = (t) => { setEditing(t); setShowModal(true); };

  const del = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"?`)) return;
    try {
      await api.deleteWaTemplate(t.id);
      toast.success('Deleted');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
  };

  const sync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.syncWaTemplates();
      toast.success(`Imported ${data.imported || 0} templates from provider`);
      load();
    } catch (e) {
      toast.info(e?.response?.data?.detail?.message || 'Could not sync — add templates manually');
    }
    setSyncing(false);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <h3 className="text-base font-semibold text-slate-800">Templates</h3>
        <span className="text-xs text-slate-500">{list.length} saved</span>
        <div className="flex-1" />
        {isPrivileged && (
          <>
            <button onClick={sync} disabled={syncing} data-testid="wa-template-sync"
                    className="px-3 py-1.5 text-xs rounded-md bg-slate-100 text-slate-700 hover:bg-slate-200 inline-flex items-center gap-1.5">
              {syncing ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Sync from Provider
            </button>
            <button onClick={openNew} data-testid="wa-template-add"
                    className="px-3 py-1.5 text-xs rounded-md bg-emerald-600 text-white hover:bg-emerald-700 inline-flex items-center gap-1.5">
              <Plus size={12} /> Add Template
            </button>
          </>
        )}
      </div>

      {loading ? <div className="py-8 text-center"><Loader2 className="mx-auto animate-spin text-slate-400" /></div> :
       list.length === 0 ? (
         <div className="text-sm text-slate-500 italic border border-dashed border-slate-200 rounded-lg p-6 text-center">
           No templates yet. Click <b>Add Template</b> to register your Meta-approved templates so they show up in the campaign dropdown.
         </div>
       ) : (
        <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
          {list.map((t) => (
            <div key={t.id} className="flex items-center gap-3 p-3">
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-slate-800 flex items-center gap-2">
                  {t.name}
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 uppercase">{t.language}</span>
                  {t.has_header_image && <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-700">image</span>}
                  {t.button_url_default && <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-50 text-purple-700">button</span>}
                </div>
                {t.description && <div className="text-xs text-slate-500 mt-0.5">{t.description}</div>}
                {(t.body_param_labels || []).length > 0 && (
                  <div className="text-xs text-slate-400 mt-0.5">
                    Params: {t.body_param_labels.map((l, i) => `{{${i + 1}}}=${l}`).join(', ')}
                  </div>
                )}
              </div>
              {isPrivileged && (
                <>
                  <button onClick={() => openEdit(t)} className="p-1.5 text-slate-500 hover:text-slate-700"><Edit2 size={14} /></button>
                  <button onClick={() => del(t)} className="p-1.5 text-slate-500 hover:text-red-600"><Trash2 size={14} /></button>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <TemplateModal
          editing={editing}
          onClose={() => setShowModal(false)}
          onSaved={() => { setShowModal(false); load(); }}
        />
      )}
    </div>
  );
}

function TemplateModal({ editing, onClose, onSaved }) {
  const [form, setForm] = useState(editing || {
    name: '', language: 'hi', description: '',
    has_header_image: false, header_image_default: '',
    button_url_default: '', body_param_labels: [], body_param_count: 0,
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      if (editing) await api.updateWaTemplate(editing.id, form);
      else await api.addWaTemplate(form);
      toast.success('Template saved');
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };

  const paramsText = (form.body_param_labels || []).join(', ');

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-lg w-full p-5 space-y-3 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h4 className="text-base font-semibold">{editing ? 'Edit Template' : 'Add Template'}</h4>
          <button onClick={onClose} className="p-1 text-slate-400"><X size={16} /></button>
        </div>
        <label className="block text-sm">
          <span className="block text-slate-600 mb-1">Template name (exact match with Meta approval)</span>
          <input type="text" value={form.name}
                 disabled={!!editing}
                 onChange={(e) => setForm({ ...form, name: e.target.value })}
                 placeholder="e.g. upsihindimedium16august"
                 className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm disabled:bg-slate-50" />
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block text-sm">
            <span className="block text-slate-600 mb-1">Language</span>
            <select value={form.language} onChange={(e) => setForm({ ...form, language: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm bg-white">
              <option value="hi">hi</option>
              <option value="en">en</option>
              <option value="en_US">en_US</option>
            </select>
          </label>
          <label className="flex items-center gap-2 text-sm mt-6">
            <input type="checkbox" checked={form.has_header_image}
                   onChange={(e) => setForm({ ...form, has_header_image: e.target.checked })} />
            Has header image
          </label>
        </div>
        <label className="block text-sm">
          <span className="block text-slate-600 mb-1">Description (optional)</span>
          <input type="text" value={form.description || ''}
                 onChange={(e) => setForm({ ...form, description: e.target.value })}
                 className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm" />
        </label>
        <label className="block text-sm">
          <span className="block text-slate-600 mb-1">Number of body variables ({'{{1}}, {{2}}, …'})</span>
          <input type="number" min="0" max="10" value={form.body_param_count ?? (form.body_param_labels?.length || 0)}
                 onChange={(e) => setForm({ ...form, body_param_count: Math.max(0, Math.min(10, parseInt(e.target.value || '0', 10))) })}
                 className="w-24 px-3 py-2 border border-slate-200 rounded-md text-sm" />
          <span className="text-[11px] text-slate-500 ml-2">Set this if you don&apos;t want to label each variable below.</span>
        </label>
        <label className="block text-sm">
          <span className="block text-slate-600 mb-1">Body param labels (optional, comma-separated, in order)</span>
          <input type="text" defaultValue={paramsText}
                 onChange={(e) => setForm({ ...form, body_param_labels: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
                 placeholder="e.g. Student Name, Batch, Fee"
                 className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm" />
        </label>
        {form.has_header_image && (
          <label className="block text-sm">
            <span className="block text-slate-600 mb-1">Default header image URL</span>
            <input type="text" value={form.header_image_default || ''}
                   onChange={(e) => setForm({ ...form, header_image_default: e.target.value })}
                   className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm" />
          </label>
        )}
        <label className="block text-sm">
          <span className="block text-slate-600 mb-1">Default URL button (optional)</span>
          <input type="text" value={form.button_url_default || ''}
                 onChange={(e) => setForm({ ...form, button_url_default: e.target.value })}
                 placeholder="https://…"
                 className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm" />
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-3 py-2 text-sm border border-slate-200 rounded-md">Cancel</button>
          <button onClick={save} disabled={saving || !form.name}
                  className="px-3 py-2 text-sm bg-emerald-600 text-white rounded-md hover:bg-emerald-700 disabled:opacity-60 inline-flex items-center gap-1.5">
            {saving && <Loader2 size={12} className="animate-spin" />} Save
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CAMPAIGNS TAB
// ─────────────────────────────────────────────────────────────────────────────
function CampaignsTab({ isPrivileged }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.listWaCampaigns();
      setList(data || []);
    } catch (e) { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [load]);

  const sendNow = async (c) => {
    if (!window.confirm(`Start sending "${c.name}" now?`)) return;
    try {
      await api.sendWaCampaignNow(c.id);
      toast.success('Campaign started');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to start'); }
  };

  const cancel = async (c) => {
    if (!window.confirm(`Cancel "${c.name}"?`)) return;
    try {
      await api.cancelWaCampaign(c.id);
      toast.success('Cancelled');
      load();
    } catch (e) { toast.error('Cancel failed'); }
  };

  const del = async (c) => {
    if (!window.confirm(`Delete "${c.name}" and its message logs? This cannot be undone.`)) return;
    try {
      await api.deleteWaCampaign(c.id);
      toast.success('Deleted');
      load();
    } catch (e) { toast.error('Delete failed'); }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <h3 className="text-base font-semibold text-slate-800">Campaigns</h3>
        <span className="text-xs text-slate-500">{list.length} total</span>
        <div className="flex-1" />
        <button onClick={() => { setEditing(null); setShowModal(true); }} data-testid="wa-campaign-add"
                className="px-3 py-1.5 text-xs rounded-md bg-emerald-600 text-white hover:bg-emerald-700 inline-flex items-center gap-1.5">
          <Plus size={12} /> New Campaign
        </button>
      </div>

      {loading ? <div className="py-8 text-center"><Loader2 className="mx-auto animate-spin text-slate-400" /></div> :
       list.length === 0 ? (
         <div className="text-sm text-slate-500 italic border border-dashed border-slate-200 rounded-lg p-6 text-center">
           No campaigns yet. Click <b>New Campaign</b> to schedule your first WhatsApp broadcast.
         </div>
       ) : (
        <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
          {list.map((c) => (
            <CampaignRow key={c.id} c={c}
                         onSendNow={() => sendNow(c)}
                         onCancel={() => cancel(c)}
                         onDelete={() => del(c)}
                         onEdit={() => { setEditing(c); setShowModal(true); }}
                         onView={() => setDetail(c.id)} />
          ))}
        </div>
      )}

      {showModal && (
        <CampaignModal
          editing={editing}
          onClose={() => setShowModal(false)}
          onSaved={() => { setShowModal(false); load(); }}
        />
      )}
      {detail && (
        <CampaignDetail campaignId={detail} onClose={() => setDetail(null)} />
      )}
    </div>
  );
}

function CampaignRow({ c, onSendNow, onCancel, onDelete, onEdit, onView }) {
  const t = c.totals || {};
  const statusCls = {
    draft: 'bg-slate-100 text-slate-600',
    scheduled: 'bg-blue-50 text-blue-700',
    running: 'bg-emerald-100 text-emerald-800 animate-pulse',
    completed: 'bg-emerald-50 text-emerald-700',
    failed: 'bg-red-50 text-red-700',
    cancelled: 'bg-amber-50 text-amber-700',
  }[c.status] || 'bg-slate-100 text-slate-600';
  return (
    <div className="p-3 flex items-center gap-3">
      <div className="min-w-0 flex-1 cursor-pointer" onClick={onView}>
        <div className="flex items-center gap-2">
          <span className="font-medium text-slate-800 truncate">{c.name}</span>
          <span className={`text-[10px] px-1.5 py-0.5 rounded uppercase ${statusCls}`}>{c.status}</span>
        </div>
        <div className="text-xs text-slate-500 mt-0.5">
          Template <code className="bg-slate-100 px-1 py-0.5 rounded text-[10px]">{c.template_name}</code>
          {' · '}
          {c.schedule_mode === 'now' && 'Manual'}
          {c.schedule_mode === 'once' && c.scheduled_at && `Once @ ${new Date(c.scheduled_at).toLocaleString('en-IN')}`}
          {c.schedule_mode === 'recurring' && c.recurrence && `${c.recurrence.frequency} @ ${c.recurrence.time}`}
        </div>
        <div className="text-xs text-slate-400 mt-0.5">
          {t.recipients ? `${t.recipients} recipients · ${t.sent || 0} sent · ${t.failed || 0} failed` : 'No sends yet'}
        </div>
      </div>
      {c.status !== 'running' && c.status !== 'cancelled' && (
        <button onClick={onSendNow} title="Send now" className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded" data-testid={`wa-send-now-${c.id}`}>
          <Play size={14} />
        </button>
      )}
      {c.status === 'running' || c.status === 'scheduled' ? (
        <button onClick={onCancel} title="Cancel" className="p-1.5 text-amber-600 hover:bg-amber-50 rounded"><Pause size={14} /></button>
      ) : null}
      <button onClick={onEdit} title="Edit" className="p-1.5 text-slate-500 hover:bg-slate-100 rounded"><Edit2 size={14} /></button>
      <button onClick={onDelete} title="Delete" className="p-1.5 text-slate-500 hover:bg-red-50 hover:text-red-600 rounded"><Trash2 size={14} /></button>
      <ChevronRight size={14} className="text-slate-300 cursor-pointer" onClick={onView} />
    </div>
  );
}

// ─── Campaign Builder Modal ─────────────────────────────────────────────────
function CampaignModal({ editing, onClose, onSaved }) {
  const [form, setForm] = useState(editing ? {
    ...editing,
    recipient_filter: { ...emptyCampaign().recipient_filter, ...(editing.recipient_filter || {}) },
    recurrence: editing.recurrence || emptyCampaign().recurrence,
  } : emptyCampaign());
  const [templates, setTemplates] = useState([]);
  const [saving, setSaving] = useState(false);
  const [manualText, setManualText] = useState('');

  useEffect(() => {
    api.listWaTemplates().then(({ data }) => setTemplates(data || [])).catch(() => setTemplates([]));
  }, []);

  const selectedTemplate = templates.find((t) => t.name === form.template_name);
  const paramLabels = selectedTemplate?.body_param_labels || [];

  const save = async () => {
    setSaving(true);
    try {
      const manualPhones = manualText.split(/[\s,;\n]+/).map(s => s.trim()).filter(Boolean);
      const payload = {
        ...form,
        template_language: form.template_language || selectedTemplate?.language || 'hi',
        recipient_filter: {
          ...form.recipient_filter,
          extra_phones: [...(form.recipient_filter.extra_phones || []), ...manualPhones],
        },
        // Only send recurrence if mode = recurring, only scheduled_at if once
        recurrence: form.schedule_mode === 'recurring' ? form.recurrence : null,
        scheduled_at: form.schedule_mode === 'once' ? (form.scheduled_at ? new Date(form.scheduled_at).toISOString() : '') : null,
      };
      if (editing) await api.updateWaCampaign(editing.id, payload);
      else await api.createWaCampaign(payload);
      toast.success(editing ? 'Campaign updated' : 'Campaign created');
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };

  const onCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const { data } = await api.uploadWaCsv(file);
      setForm((f) => ({
        ...f,
        recipient_filter: {
          ...f.recipient_filter,
          sources: [...new Set([...(f.recipient_filter.sources || []), 'csv'])],
          extra_phones: [...(f.recipient_filter.extra_phones || []), ...(data.phones || [])],
        },
      }));
      toast.success(`Parsed ${data.count} phones from CSV`);
    } catch (err) { toast.error('CSV upload failed'); }
    e.target.value = '';
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-3xl w-full p-5 space-y-4 max-h-[92vh] overflow-y-auto" data-testid="wa-campaign-modal">
        <div className="flex items-center justify-between">
          <h4 className="text-base font-semibold">{editing ? 'Edit Campaign' : 'New WhatsApp Campaign'}</h4>
          <button onClick={onClose} className="p-1 text-slate-400"><X size={16} /></button>
        </div>

        {/* Name + template */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className="text-sm">
            <span className="block text-slate-600 mb-1">Campaign name</span>
            <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                   data-testid="wa-campaign-name"
                   className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm" />
          </label>
          <label className="text-sm">
            <span className="block text-slate-600 mb-1">Template</span>
            <select value={form.template_name} onChange={(e) => {
              const t = templates.find((x) => x.name === e.target.value);
              const paramCount = t?.body_param_labels?.length || t?.body_param_count || 0;
              setForm({
                ...form,
                template_name: e.target.value,
                template_language: t?.language || form.template_language,
                header_image: t?.header_image_default || '',
                button_url: t?.button_url_default || '',
                body_params: Array.from({ length: paramCount }, (_, i) =>
                  i === 0 ? '{{lead.name}}' : ''
                ),
              });
            }} data-testid="wa-campaign-template" className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm bg-white">
              <option value="">-- select a template --</option>
              {templates.map((t) => <option key={t.id} value={t.name}>{t.name} ({t.language})</option>)}
            </select>
          </label>
        </div>
        {templates.length === 0 && (
          <div className="text-xs bg-amber-50 text-amber-800 rounded-md p-2 flex items-center gap-2">
            <AlertCircle size={12} /> No templates saved. Go to the <b>Templates</b> tab first, then come back here.
          </div>
        )}

        {/* MESSAGE CONTENT — always visible with clear guidance */}
        <div className="border border-slate-200 rounded-lg p-4 bg-slate-50 space-y-3">
          <div className="flex items-center justify-between">
            <div className="text-sm font-semibold text-slate-800 flex items-center gap-2">
              <MessageCircle size={14} /> Message Content
            </div>
            <span className="text-[11px] text-slate-500">
              WhatsApp requires a Meta-approved template. Fill only its variables below.
            </span>
          </div>

          {!form.template_name ? (
            <div className="bg-white border border-dashed border-slate-300 rounded-md p-4 text-center text-sm text-slate-500">
              Pick a template above to fill its variables.
            </div>
          ) : (
            <>
              {/* Show a text hint about how many params this template has */}
              {selectedTemplate?.description && (
                <div className="text-[11px] text-slate-600 italic">{selectedTemplate.description}</div>
              )}

              {/* If neither labels nor count are set on the template, let user declare how many */}
              {!(selectedTemplate?.body_param_labels?.length) && !(selectedTemplate?.body_param_count) && (
                <div className="text-xs bg-amber-50 border border-amber-200 rounded p-2 space-y-2">
                  <div className="text-amber-800">
                    This template has no variable definitions. If the Meta-approved template has body variables
                    (like <code className="px-1 rounded bg-white">{'{{1}}'}</code>, <code className="px-1 rounded bg-white">{'{{2}}'}</code>),
                    set how many below — or leave 0 if the template body is fully static.
                  </div>
                  <label className="text-xs inline-flex items-center gap-2">
                    Number of body variables:
                    <input type="number" min="0" max="10"
                           value={form.body_params.length}
                           onChange={(e) => {
                             const n = Math.max(0, Math.min(10, parseInt(e.target.value || '0', 10)));
                             const arr = Array.from({ length: n }, (_, i) => form.body_params[i] || (i === 0 ? '{{lead.name}}' : ''));
                             setForm({ ...form, body_params: arr });
                           }}
                           className="w-16 px-2 py-1 border border-slate-200 rounded" />
                  </label>
                </div>
              )}

              {/* Render one input per body param */}
              {form.body_params.length > 0 && (
                <div className="space-y-2">
                  <div className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Body Variables</div>
                  {form.body_params.map((val, idx) => {
                    const label = paramLabels[idx] || `Variable {{${idx + 1}}}`;
                    return (
                      <div key={idx} className="grid grid-cols-1 md:grid-cols-[130px_1fr] gap-2 items-center bg-white p-2 rounded border border-slate-200">
                        <div className="text-xs font-medium text-slate-700">
                          <code className="bg-slate-100 px-1 py-0.5 rounded">{`{{${idx + 1}}}`}</code>
                          <span className="ml-1 text-slate-500">{paramLabels[idx] ? label : ''}</span>
                        </div>
                        <input type="text" value={val || ''}
                               onChange={(e) => {
                                 const arr = [...form.body_params];
                                 arr[idx] = e.target.value;
                                 setForm({ ...form, body_params: arr });
                               }}
                               placeholder={idx === 0 ? "e.g. {{lead.name}} or type a static value" : "Type a value or use {{lead.<field>}}"}
                               data-testid={`wa-body-param-${idx}`}
                               className="w-full px-2 py-1.5 border border-slate-200 rounded text-sm" />
                      </div>
                    );
                  })}
                </div>
              )}

              {form.body_params.length === 0 && (selectedTemplate?.body_param_labels?.length === 0) && (selectedTemplate?.body_param_count === 0) && (
                <div className="text-xs text-slate-500 italic">This template body has no variables — it will send as-is.</div>
              )}

              {/* Placeholder hint */}
              <div className="text-[11px] text-slate-500 flex flex-wrap items-center gap-2">
                <span>Available placeholders:</span>
                {['{{lead.name}}', '{{lead.course}}', '{{lead.email}}', '{{lead.status}}', '{{lead.phone}}'].map(p => (
                  <code key={p} className="bg-white border border-slate-200 px-1.5 py-0.5 rounded cursor-pointer hover:bg-slate-100"
                        onClick={() => {
                          // Copy to clipboard for convenience
                          try { navigator.clipboard.writeText(p); toast.success(`Copied ${p}`); } catch (_e) { /* clipboard unavailable */ }
                        }}>{p}</code>
                ))}
              </div>

              {/* Header image + button URL */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2 border-t border-slate-200">
                <label className="text-sm">
                  <span className="block text-slate-600 mb-1 text-xs">
                    Header image URL {selectedTemplate?.has_header_image ? '(required by template)' : '(optional)'}
                  </span>
                  <input type="text" value={form.header_image || ''}
                         onChange={(e) => setForm({ ...form, header_image: e.target.value })}
                         placeholder="https://…"
                         className="w-full px-2 py-1.5 border border-slate-200 rounded text-sm" />
                </label>
                <label className="text-sm">
                  <span className="block text-slate-600 mb-1 text-xs">Button URL parameter (optional)</span>
                  <input type="text" value={form.button_url || ''}
                         onChange={(e) => setForm({ ...form, button_url: e.target.value })}
                         placeholder="https://your.app/…"
                         className="w-full px-2 py-1.5 border border-slate-200 rounded text-sm" />
                </label>
              </div>

              {/* Live preview */}
              <MessagePreview form={form} />
            </>
          )}
        </div>

        {/* Recipients */}
        <RecipientPicker
          value={form.recipient_filter}
          manualText={manualText}
          onManualChange={setManualText}
          onChange={(next) => setForm({ ...form, recipient_filter: next })}
          onCsv={onCsv}
        />

        {/* Scheduling */}
        <div className="border-t border-slate-100 pt-3 space-y-2">
          <div className="text-xs font-semibold text-slate-700 flex items-center gap-2">
            <Clock size={12} /> Scheduling
          </div>
          <div className="flex flex-wrap gap-2 text-xs">
            {[
              { id: 'now', label: 'Manual (send now button)' },
              { id: 'once', label: 'One-time' },
              { id: 'recurring', label: 'Recurring' },
            ].map((opt) => (
              <button key={opt.id} onClick={() => setForm({ ...form, schedule_mode: opt.id })} data-testid={`wa-schedule-${opt.id}`}
                      className={`px-2.5 py-1 rounded border ${form.schedule_mode === opt.id ? 'bg-emerald-50 border-emerald-500 text-emerald-800' : 'bg-white border-slate-200 text-slate-600'}`}>
                {opt.label}
              </button>
            ))}
          </div>
          {form.schedule_mode === 'once' && (
            <input type="datetime-local" value={form.scheduled_at ? form.scheduled_at.slice(0, 16) : ''}
                   onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })}
                   className="px-3 py-2 border border-slate-200 rounded-md text-sm" />
          )}
          {form.schedule_mode === 'recurring' && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
              <select value={form.recurrence.frequency}
                      onChange={(e) => setForm({ ...form, recurrence: { ...form.recurrence, frequency: e.target.value } })}
                      className="px-2 py-1.5 border border-slate-200 rounded bg-white">
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </select>
              <input type="time" value={form.recurrence.time}
                     onChange={(e) => setForm({ ...form, recurrence: { ...form.recurrence, time: e.target.value } })}
                     className="px-2 py-1.5 border border-slate-200 rounded" />
              <div className="text-slate-500 self-center">UTC · re-checked every minute</div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-3 border-t">
          <button onClick={onClose} className="px-3 py-2 text-sm border border-slate-200 rounded-md">Cancel</button>
          <button onClick={save} disabled={saving || !form.name || !form.template_name} data-testid="wa-campaign-save"
                  className="px-3 py-2 text-sm bg-emerald-600 text-white rounded-md hover:bg-emerald-700 disabled:opacity-60 inline-flex items-center gap-1.5">
            {saving && <Loader2 size={12} className="animate-spin" />} {editing ? 'Save Changes' : 'Create Campaign'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Campaign detail (progress + logs) ──────────────────────────────────────
function CampaignDetail({ campaignId, onClose }) {
  const [camp, setCamp] = useState(null);
  const [messages, setMessages] = useState({ messages: [], total: 0 });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [c, m] = await Promise.all([
        api.getWaCampaign(campaignId),
        api.listWaCampaignMessages(campaignId, { page: 1, limit: 200 }),
      ]);
      setCamp(c.data);
      setMessages(m.data);
    } catch (e) { /* ignore */ }
    setLoading(false);
  }, [campaignId]);

  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [load]);

  const totals = camp?.totals || { queued: 0, sent: 0, failed: 0, recipients: 0 };
  const progress = totals.recipients > 0 ? Math.round(((totals.sent + totals.failed) * 100) / totals.recipients) : 0;

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-3xl w-full p-5 space-y-3 max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h4 className="text-base font-semibold">{camp?.name || 'Loading…'}</h4>
          <button onClick={onClose} className="p-1 text-slate-400"><X size={16} /></button>
        </div>
        {loading || !camp ? <Loader2 className="animate-spin mx-auto text-slate-400" /> : (
          <>
            <div className="grid grid-cols-4 gap-2 text-xs">
              <Stat label="Recipients" value={totals.recipients} />
              <Stat label="Sent" value={totals.sent} tone="ok" />
              <Stat label="Failed" value={totals.failed} tone={totals.failed ? 'err' : ''} />
              <Stat label="Status" value={camp.status} />
            </div>
            <div className="w-full bg-slate-100 rounded-full h-2">
              <div className="bg-emerald-500 h-2 rounded-full transition-all" style={{ width: `${progress}%` }} />
            </div>
            <div className="text-xs text-slate-500">
              Template <code className="bg-slate-100 px-1 rounded">{camp.template_name}</code> ({camp.template_language})
              {camp.last_run_at && ` · Last run ${new Date(camp.last_run_at).toLocaleString('en-IN')}`}
              {camp.next_run_at && ` · Next ${new Date(camp.next_run_at).toLocaleString('en-IN')}`}
              {camp.last_error && <div className="text-red-600 mt-1">Error: {camp.last_error}</div>}
            </div>
            <div className="text-xs font-semibold text-slate-700 mt-2">Delivery log ({messages.total} messages)</div>
            <div className="border border-slate-200 rounded-lg divide-y divide-slate-100 max-h-64 overflow-y-auto">
              {messages.messages.length === 0 ? (
                <div className="p-3 text-xs text-slate-400 italic">No messages sent yet.</div>
              ) : messages.messages.map((m) => (
                <div key={m.id} className="p-2 flex items-center gap-2 text-xs">
                  {m.status === 'sent' ? <CheckCircle2 size={12} className="text-emerald-600" /> : <XCircle size={12} className="text-red-500" />}
                  <span className="font-mono">{m.phone}</span>
                  {m.name && <span className="text-slate-500">· {m.name}</span>}
                  <span className="flex-1" />
                  <span className={`text-[10px] px-1.5 py-0.5 rounded ${m.status === 'sent' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
                    {m.status} · {m.response_code}
                  </span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, tone }) {
  const cls = tone === 'ok' ? 'text-emerald-600' : tone === 'err' ? 'text-red-600' : 'text-slate-700';
  return (
    <div className="bg-slate-50 rounded-md p-2 text-center">
      <div className={`text-lg font-semibold ${cls}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Live message preview — renders a WhatsApp-style card with placeholders resolved
// against a fake sample lead so admins see exactly what will get sent.
// ─────────────────────────────────────────────────────────────────────────────
function MessagePreview({ form }) {
  const sample = { name: 'Rahul Sharma', course: 'UPSI Final Merit Batch', email: 'rahul@example.com', status: 'new', phone: '9876543210' };
  const resolve = (v) => (v || '').replace(/\{\{\s*lead\.([\w]+)\s*\}\}/g, (_, k) => sample[k] ?? '');
  const bodyText = (form.body_params || []).map((v, i) => `{{${i + 1}}} → ${resolve(v) || '—'}`).join('\n');
  const image = resolve(form.header_image);
  const button = resolve(form.button_url);
  return (
    <div className="pt-3 border-t border-slate-200">
      <div className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide mb-2">Preview (sample recipient: Rahul Sharma)</div>
      <div className="max-w-sm ml-auto bg-emerald-50 border border-emerald-200 rounded-2xl p-3 shadow-sm">
        {image && (
          <img src={image} alt="preview header" className="w-full rounded-lg mb-2 object-cover max-h-32"
               onError={(e) => { e.currentTarget.style.display = 'none'; }} />
        )}
        <div className="text-xs text-slate-700 whitespace-pre-line font-mono">
          [Template: <b>{form.template_name}</b>]
          {bodyText && `\n\n${bodyText}`}
          {!bodyText && '\n\n(No body variables)'}
        </div>
        {button && (
          <div className="mt-2 text-center text-xs font-semibold text-emerald-700 border-t border-emerald-200 pt-2 truncate">
            🔗 {button}
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Recipient Picker — Leads-section-style filter with live preview
// ─────────────────────────────────────────────────────────────────────────────
function RecipientPicker({ value, onChange, manualText, onManualChange, onCsv }) {
  const [meta, setMeta] = useState({ statuses: [], courses: [], groups: [], agents: [], totals: { active: 0, archived: 0 } });
  const [count, setCount] = useState(null);
  const [sample, setSample] = useState([]);
  const [loading, setLoading] = useState(false);

  // Load filter dropdown data once
  useEffect(() => {
    api.getWaLeadFilters().then(({ data }) => setMeta(data)).catch(() => {});
  }, []);

  const patch = useCallback((diff) => onChange({ ...value, ...diff }), [value, onChange]);

  const sources = value.sources || [];
  const hasLeads = sources.includes('leads');
  const hasGroups = sources.includes('groups');
  const hasCsv = sources.includes('csv');
  const hasManual = sources.includes('manual');

  const toggleSource = (src) => {
    const set = new Set(sources);
    if (set.has(src)) set.delete(src); else set.add(src);
    patch({ sources: [...set] });
  };

  // Live-preview (debounced) whenever any filter changes
  useEffect(() => {
    let cancel = false;
    const handle = setTimeout(async () => {
      setLoading(true);
      try {
        const manualPhones = (manualText || '').split(/[\s,;\n]+/).map(s => s.trim()).filter(Boolean);
        const filter = { ...value, extra_phones: [...(value.extra_phones || []), ...manualPhones] };
        const { data } = await api.previewWaRecipients(filter);
        if (!cancel) { setCount(data.count); setSample(data.sample || []); }
      } catch (e) {
        if (!cancel) { setCount(0); setSample([]); }
      }
      if (!cancel) setLoading(false);
    }, 400);
    return () => { cancel = true; clearTimeout(handle); };
  }, [JSON.stringify(value), manualText]);

  const toggleGroup = (gid) => {
    const set = new Set(value.group_ids || []);
    if (set.has(gid)) set.delete(gid); else set.add(gid);
    patch({ group_ids: [...set] });
  };

  const clearAll = () => {
    onChange({
      sources: [], lead_status: '', lead_course: '', lead_courses: [], lead_from_date: '', lead_to_date: '',
      lead_assigned_to: '', group_ids: [], extra_phones: [], dedupe: true,
    });
    onManualChange('');
  };

  return (
    <div className="border-t border-slate-100 pt-3 space-y-3">
      <div className="flex items-center gap-2">
        <div className="text-xs font-semibold text-slate-700 flex items-center gap-2">
          <Users size={12} /> Recipients
        </div>
        <span className="text-[11px] text-slate-500">Choose one or more sources below</span>
        <div className="flex-1" />
        {(sources.length > 0 || manualText) && (
          <button onClick={clearAll} className="text-[11px] text-slate-500 hover:text-red-600">Clear all</button>
        )}
      </div>

      {/* Source toggles as big cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <SourceCard active={hasLeads} onClick={() => toggleSource('leads')} testId="wa-source-leads"
                    label="From Leads" hint={`${(meta.totals?.active || 0).toLocaleString()} leads`} />
        <SourceCard active={hasGroups} onClick={() => toggleSource('groups')} testId="wa-source-groups"
                    label="Lead Groups" hint={`${meta.groups?.length || 0} groups`} />
        <SourceCard active={hasCsv} onClick={() => toggleSource('csv')} testId="wa-source-csv"
                    label="Upload CSV" hint="Bulk import" />
        <SourceCard active={hasManual} onClick={() => toggleSource('manual')} testId="wa-source-manual"
                    label="Type numbers" hint="Comma / new-line" />
      </div>

      {/* Leads filter block — populated from real leads data (like Leads section) */}
      {hasLeads && (
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-2">
          <div className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Lead filters (blank = ALL leads)</div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2 text-xs">
            <label>
              <span className="block text-slate-500 mb-1">Status</span>
              <select value={value.lead_status || ''} onChange={(e) => patch({ lead_status: e.target.value })}
                      data-testid="wa-lead-status"
                      className="w-full px-2 py-1.5 border border-slate-200 rounded bg-white">
                <option value="">All statuses</option>
                {(meta.statuses || []).map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
            <label>
              <span className="block text-slate-500 mb-1">Courses</span>
              <CourseMultiSelect
                options={meta.courses || []}
                selected={value.lead_courses || []}
                onChange={(next) => patch({ lead_courses: next, lead_course: '' })}
              />
            </label>
            <label>
              <span className="block text-slate-500 mb-1">Assigned agent</span>
              <select value={value.lead_assigned_to || ''} onChange={(e) => patch({ lead_assigned_to: e.target.value })}
                      className="w-full px-2 py-1.5 border border-slate-200 rounded bg-white">
                <option value="">Any agent</option>
                {(meta.agents || []).map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </label>
            <label className="flex items-end gap-1">
              <span className="sr-only">De-dupe</span>
              <label className="text-xs inline-flex items-center gap-1 pb-1.5">
                <input type="checkbox" checked={value.dedupe !== false}
                       onChange={(e) => patch({ dedupe: e.target.checked })} />
                De-duplicate phones
              </label>
            </label>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
            <label>
              <span className="block text-slate-500 mb-1">Created from</span>
              <input type="date" value={value.lead_from_date || ''}
                     onChange={(e) => patch({ lead_from_date: e.target.value })}
                     className="w-full px-2 py-1.5 border border-slate-200 rounded" />
            </label>
            <label>
              <span className="block text-slate-500 mb-1">Created to</span>
              <input type="date" value={value.lead_to_date || ''}
                     onChange={(e) => patch({ lead_to_date: e.target.value })}
                     className="w-full px-2 py-1.5 border border-slate-200 rounded" />
            </label>
          </div>
        </div>
      )}

      {/* Lead groups */}
      {hasGroups && (
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-2">
          <div className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Pick lead groups</div>
          {(meta.groups || []).length === 0 ? (
            <div className="text-xs text-slate-500 italic">No lead groups exist yet. Create one from the Leads section first.</div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {meta.groups.map((g) => {
                const on = (value.group_ids || []).includes(g.id);
                return (
                  <button key={g.id} onClick={() => toggleGroup(g.id)}
                          className={`px-2.5 py-1 text-xs rounded border ${on ? 'bg-emerald-50 border-emerald-500 text-emerald-800' : 'bg-white border-slate-200 text-slate-600'}`}>
                    {g.name} <span className="text-[10px] opacity-70">· {g.count}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* CSV upload */}
      {hasCsv && (
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-2">
          <div className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Upload CSV / TXT</div>
          <label className="inline-flex items-center gap-1.5 text-xs cursor-pointer px-3 py-1.5 bg-white border border-slate-200 rounded hover:bg-slate-50">
            <Upload size={12} /> Choose file
            <input type="file" accept=".csv,.txt" onChange={onCsv} className="hidden" data-testid="wa-csv-upload" />
          </label>
          {(value.extra_phones || []).length > 0 && (
            <div className="text-[11px] text-slate-600">
              {value.extra_phones.length.toLocaleString()} numbers loaded
              <button onClick={() => patch({ extra_phones: [] })}
                      className="ml-2 text-red-500 hover:underline">clear</button>
            </div>
          )}
        </div>
      )}

      {/* Manual list */}
      {hasManual && (
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-2">
          <div className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Type phone numbers</div>
          <textarea rows={3} value={manualText} onChange={(e) => onManualChange(e.target.value)}
                    placeholder="9876543210, 918010085100  (comma or new-line separated)"
                    className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded" />
        </div>
      )}

      {/* Live count + sample preview (WhatsApp-style) */}
      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
        <div className="flex items-center gap-2 mb-2">
          {loading ? <Loader2 size={14} className="animate-spin text-emerald-700" /> : <Users size={14} className="text-emerald-700" />}
          <span className="text-sm font-semibold text-emerald-800" data-testid="wa-recipient-count">
            {count === null ? 'Choose a source above…' : `${count.toLocaleString()} unique recipients selected`}
          </span>
        </div>
        {sample.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {sample.map((r, i) => (
              <span key={i} className="text-[10px] px-2 py-0.5 bg-white border border-emerald-200 rounded-full text-slate-700">
                {r.name || 'Unknown'} · {r.phone}
              </span>
            ))}
            {count > sample.length && (
              <span className="text-[10px] text-slate-500 italic self-center">+{(count - sample.length).toLocaleString()} more…</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function SourceCard({ active, onClick, label, hint, testId }) {
  return (
    <button onClick={onClick} data-testid={testId}
            className={`text-left border rounded-lg p-3 transition-colors ${
              active ? 'bg-emerald-50 border-emerald-500 shadow-sm' : 'bg-white border-slate-200 hover:border-slate-300'
            }`}>
      <div className="flex items-center gap-2">
        <div className={`w-4 h-4 rounded-full border-2 flex-shrink-0 ${active ? 'bg-emerald-600 border-emerald-600' : 'border-slate-300'}`}>
          {active && <CheckCircle2 size={14} className="text-white -m-0.5" />}
        </div>
        <div className="text-sm font-medium text-slate-800">{label}</div>
      </div>
      <div className="text-[10px] text-slate-500 mt-1 ml-6">{hint}</div>
    </button>
  );
}


// Multi-select course picker — checkbox dropdown with search
function CourseMultiSelect({ options, selected, onChange }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = React.useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const filtered = options.filter((c) => c.toLowerCase().includes(search.toLowerCase()));
  const toggle = (c) => {
    const set = new Set(selected);
    if (set.has(c)) set.delete(c); else set.add(c);
    onChange([...set]);
  };
  const clear = (e) => { e.stopPropagation(); onChange([]); };

  const label = selected.length === 0
    ? 'All courses'
    : selected.length === 1
      ? selected[0]
      : `${selected.length} courses selected`;

  return (
    <div className="relative" ref={ref} data-testid="wa-lead-courses">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full text-left px-2 py-1.5 border border-slate-200 rounded bg-white flex items-center justify-between gap-2"
        title={selected.length > 1 ? selected.join(', ') : label}
      >
        <span className="truncate">{label}</span>
        <span className="flex items-center gap-1">
          {selected.length > 0 && (
            <span onClick={clear} className="text-slate-400 hover:text-red-600 text-[10px]">clear</span>
          )}
          <ChevronRight size={12} className={`transition-transform ${open ? 'rotate-90' : ''}`} />
        </span>
      </button>
      {open && (
        <div className="absolute left-0 right-0 mt-1 z-30 bg-white border border-slate-200 rounded-md shadow-lg max-h-64 flex flex-col">
          <div className="p-2 border-b border-slate-100">
            <input
              autoFocus
              type="text"
              placeholder="Search courses…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full px-2 py-1 text-xs border border-slate-200 rounded"
            />
          </div>
          <div className="overflow-y-auto">
            {filtered.length === 0 && (
              <div className="px-3 py-2 text-xs text-slate-500">No matches</div>
            )}
            {filtered.map((c) => {
              const checked = selected.includes(c);
              return (
                <label
                  key={c}
                  className="flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-slate-50 cursor-pointer"
                  data-testid={`wa-course-opt-${c}`}
                >
                  <input type="checkbox" checked={checked} onChange={() => toggle(c)} />
                  <span className="truncate">{c}</span>
                </label>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
